import { EventForGetEventsResponse } from 'App/api/endpoints/events/responses';
import React, { useEffect, useRef, useState } from 'react';
import moment, { Moment } from 'moment';
import i18n from 'i18n';
import { EventUtils } from '../../utils/EventUtils';
import { Col, Row, Spin, Tag } from 'antd';
import { EventPreview } from '../eventPreview/EventPreview';

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';

import '@fullcalendar/core/main.css';
import '@fullcalendar/daygrid/main.css';
import '@fullcalendar/timegrid/main.css';

import './EventCalendar.less';
import { useTranslation } from 'react-i18next';
import {
	key_Drift,
	key_JamboreesAndSpots,
	key_Others,
	key_Races,
	key_Rallies,
	key_RcModels,
} from 'App/common/utils/eventCategoryGrouping.utils';

interface EventCalendarProps {
	events: EventForGetEventsResponse[];
	startDate?: Moment;
	endDate?: Moment;
	loading?: boolean;
	onChange?: (start: Moment, end: Moment) => void;
}

export const EventCalendar = ({ events, startDate, endDate, loading, onChange }: EventCalendarProps) => {
	const calendarRef = useRef(null);
	const { t } = useTranslation('models');

	const [fcEvents, setFcEvents] = useState([]);
	const [previewEvent, setPreviewEvent] = useState<EventForGetEventsResponse>(null);

	useEffect(() => {
		const mappedEvents = events.map((event) => {
			const color = EventUtils.categoryColor(event.categories[0]);
			return {
				id: event.id,
				title: event.title,
				start: moment(event.dateStart).format('YYYY-MM-DDTHH:mm:ss'),
				end: moment(event.dateEnd).format('YYYY-MM-DDTHH:mm:ss'),
				backgroundColor: color,
			};
		});

		setFcEvents(mappedEvents);
	}, [events]);

	useEffect(() => {
		const dateBetween = getMidTimeBetweenTwoDate(startDate, endDate);
		const api = calendarRef.current.getApi();
		api.gotoDate(dateBetween.format('YYYY-MM-DD'));
	}, [startDate, endDate]);

	const getMidTimeBetweenTwoDate = (date1, date2): Moment => {
		let date1Moment = moment(date1);
		let date2Moment = moment(date2);
		let diff = date2Moment.diff(date1Moment, 'minutes');
		let midTime = date1Moment.add(diff / 2, 'minutes');
		return moment(midTime.format('YYYY-MM-DD'));
	};

	const renderEventContent = (eventInfo) => {
		return (
			<div
				className='event-tile'
				style={{
					backgroundColor: eventInfo.event.backgroundColor,
					padding: '2px 0px',
				}}
			>
				<i>{eventInfo.event.title}</i>
			</div>
		);
	};

	const onDatesSet = (params) => {
		if (!onChange) return;

		onChange(moment(params.startStr, 'YYYY-MM-DD:HH:mm'), moment(params.endStr, 'YYYY-MM-DD:HH:mm'));
	};

	const onEventClick = (tile) => {
		setPreviewEvent(events.find((e) => e.id === tile.event.id));
	};

	const onModalCancel = () => {
		setPreviewEvent(null);
	};

	const categoryPill = (text: string, key: string) => {
		return (
			<Col className='category-pill' span={4} xs={{ span: 8 }} md={{ span: 8 }} lg={{ span: 4 }}>
				<Tag color={EventUtils.categoryGroupColor(key)} style={{ width: '100%' }}>
					<span>{text}</span>
				</Tag>
			</Col>
		);
	};

	return (
		<div className='event-calendar'>
			<Spin spinning={loading}>
				<Row gutter={[16, 8]} style={{ marginBottom: 20 }}>
					{categoryPill(t('Event.Category.Rallies'), key_Rallies)}
					{categoryPill(t('Event.Category.Races'), key_Races)}
					{categoryPill(t('Event.Category.Drift'), key_Drift)}
					{categoryPill(t('Event.Category.RcModels'), key_RcModels)}
					{categoryPill(t('Event.Category.Others'), key_Others)}
					{categoryPill(t('Event.Category.JamboreesAndMeetups'), key_JamboreesAndSpots)}
				</Row>
				<FullCalendar
					initialView='dayGridMonth'
					headerToolbar={{
						left: 'prev',
						center: 'title',
						right: 'next',
					}}
					themeSystem='Simplex'
					plugins={[dayGridPlugin]}
					contentHeight='auto'
					locale={i18n.language}
					ref={calendarRef}
					firstDay={1}
					events={fcEvents}
					eventContent={renderEventContent}
					datesSet={onDatesSet}
					eventClick={onEventClick}
				/>
			</Spin>
			<EventPreview event={previewEvent} onCancel={onModalCancel} />
		</div>
	);
};
